import { createContext, useState, useCallback, useContext } from 'react';
import { TourOptionDto } from '@/features/dashboard/tourOptions/types/TourOptionDto.ts';
import { Outlet } from '@tanstack/react-router';
import TourDto from '@/features/dashboard/tourConfig/types/TourDto.ts';
import { TourPriceDto } from '@/features/dashboard/tourConfig/types/TourPriceDto.ts';

interface TourBuilderState {
  currentStep: number;
  tour: TourDto;
  tourOptions: TourOptionDto[];
  basePrice: TourPriceDto[];
}

interface TourBuilderContextType {
  state: Partial<TourBuilderState>;
  updateTourBuilderState: (newData: Partial<TourBuilderState>) => void;
  clearTourBuilderState: () => void;
  setTourOptions: (options: TourOptionDto[]) => void;
  setBasePrice: (basePrice: TourPriceDto) => void;
}

const TourBuilderContext = createContext<TourBuilderContextType | undefined>(
  undefined,
);

const TOUR_BUILDER_CONFIG = 'tourBuilderConfig';

export const TourBuilderProvider = () => {
  const [state, setState] = useState<Partial<TourBuilderState>>(() => {
    try {
      const savedDraft = sessionStorage.getItem(TOUR_BUILDER_CONFIG);
      return savedDraft ? JSON.parse(savedDraft) : {};
    } catch (error) {
      console.error('Error reading tour draft from sessionStorage:', error);
      return {
        currentStep: 1,
      };
    }
  });

  const updateTourBuilderState = useCallback(
    (newData: Partial<TourBuilderState>) => {
      setState((prevState) => {
        const updatedState = { ...prevState, ...newData };
        sessionStorage.setItem(
          TOUR_BUILDER_CONFIG,
          JSON.stringify(updatedState),
        );
        return updatedState;
      });
    },
    [],
  );

  const setTourOptions = useCallback((options: TourOptionDto[]) => {
    setState((prevState) => {
      const updatedState = { ...prevState, tourOptions: options };
      sessionStorage.setItem(TOUR_BUILDER_CONFIG, JSON.stringify(updatedState));
      return updatedState;
    });
  }, []);

  const clearTourBuilderState = useCallback(() => {
    setState({});
    sessionStorage.removeItem(TOUR_BUILDER_CONFIG);
  }, []);

  const setBasePrice = useCallback((basePrice: TourPriceDto) => {
    setState((prevState) => {
      const updatedBasePrice = [...(prevState.basePrice || []), basePrice];
      const updatedState = { ...prevState, basePrice: updatedBasePrice };
      sessionStorage.setItem(TOUR_BUILDER_CONFIG, JSON.stringify(updatedState));
      return updatedState;
    });
  }, []);

  const value: TourBuilderContextType = {
    state,
    updateTourBuilderState,
    clearTourBuilderState,
    setBasePrice,
    setTourOptions,
  };

  return (
    <TourBuilderContext.Provider value={value}>
      <Outlet />
    </TourBuilderContext.Provider>
  );
};

export const useTourBuilderContext = (): TourBuilderContextType => {
  const context = useContext(TourBuilderContext);
  if (context === undefined) {
    throw new Error('useTourBuilder must be used within a TourBuilderProvider');
  }
  return context;
};
